import { lazy, Suspense, type ReactNode } from 'react'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'

import { AppShell } from './components/AppShell'
import { LoadingState } from './components/Feedback'
import { ToastProvider } from './components/Toasts'
import { AuthProvider } from './features/auth/AuthProvider'
import { useAuth } from './features/auth/authContext'
import { HotkeyProvider } from './hotkeys/HotkeyProvider'

const OverviewPage = lazy(() => import('./features/overview/OverviewPage'))
const HomePage = lazy(() => import('./features/marketing/HomePage'))
const GuidesPage = lazy(() => import('./features/marketing/GuidesPage'))
const GuidePage = lazy(() => import('./features/marketing/GuidePage'))
const PrivacyPage = lazy(() => import('./features/marketing/PrivacyPage'))
const TermsPage = lazy(() => import('./features/marketing/TermsPage'))
const ScreeningPage = lazy(() => import('./features/screening/ScreeningPage'))
const AnalysisPage = lazy(() => import('./features/analysis/AnalysisPage'))
const BacktestingPage = lazy(() => import('./features/backtesting/BacktestingPage'))
const PortfolioPage = lazy(() => import('./features/portfolio/PortfolioPage'))
const PipelinePage = lazy(() => import('./features/pipeline/PipelinePage'))
const FilingsPage = lazy(() => import('./features/filings/FilingsPage'))
const FilingViewerPage = lazy(() => import('./features/filings/FilingViewerPage'))
const EventsPage = lazy(() => import('./features/events/EventsPage'))
const ComparisonPage = lazy(() => import('./features/comparison/ComparisonPage'))
const ResearchPage = lazy(() => import('./features/research/ResearchPage'))
const BondsPage = lazy(() => import('./features/bonds/BondsPage'))
const LoginPage = lazy(() => import('./features/auth/LoginPage'))
const AccountPage = lazy(() => import('./features/auth/AccountPage'))
const VerifyEmailPage = lazy(() => import('./features/auth/VerifyEmailPage'))
const ForgotPasswordPage = lazy(() => import('./features/auth/ForgotPasswordPage'))
const AdminPage = lazy(() => import('./features/auth/AdminPage'))
const ChatPage = lazy(() => import('./features/chat/ChatPage'))
const ProfilePage = lazy(() => import('./features/chat/ProfilePage'))
const ApiDocsPage = lazy(() => import('./features/apidocs/ApiDocsPage'))

function WorkspaceLayout() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  )
}

function AdminOnlyPage({ children }: { children: ReactNode }) {
  const auth = useAuth()
  if (auth.loading) return <LoadingState label="Checking admin access" />
  if (auth.user?.role !== 'admin') return <Navigate replace to="/overview" />
  return children
}

export function App() {
  return (
    <AuthProvider>
      <HotkeyProvider>
      <ToastProvider>
      <Suspense fallback={<LoadingState label="Loading page" />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/guides" element={<GuidesPage />} />
          <Route path="/guides/:slug" element={<GuidePage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/pricing" element={<Navigate replace to="/" />} />
          <Route path="/login" element={<LoginPage key="login" initialMode="login" />} />
          <Route path="/register" element={<LoginPage key="register" initialMode="register" />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/security" element={<Navigate replace to="/analyze" />} />
          <Route path="/backtesting" element={<Navigate replace to="/backtest" />} />

          <Route element={<WorkspaceLayout />}>
            <Route path="/overview" element={<OverviewPage />} />
            <Route path="/screen" element={<ScreeningPage />} />
            <Route path="/analyze" element={<AnalysisPage />} />
            <Route path="/analyze/:companyCode" element={<AnalysisPage />} />
            <Route path="/backtest" element={<BacktestingPage />} />
            <Route path="/portfolio" element={<PortfolioPage />} />
            <Route path="/pipeline" element={<AdminOnlyPage><PipelinePage /></AdminOnlyPage>} />
            <Route path="/filings" element={<FilingsPage />} />
            <Route path="/filings/:docId" element={<FilingViewerPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/compare" element={<ComparisonPage />} />
            <Route path="/research" element={<ResearchPage />} />
            <Route path="/bonds" element={<BondsPage />} />
            <Route path="/chat" element={<ChatPage />} />
            <Route path="/people/:username" element={<ProfilePage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/api-docs" element={<ApiDocsPage />} />
            <Route path="/api-docs/:sectionId" element={<ApiDocsPage />} />
          </Route>

          <Route path="*" element={<Navigate replace to="/" />} />
        </Routes>
      </Suspense>
      </ToastProvider>
      </HotkeyProvider>
    </AuthProvider>
  )
}
